/**
 * LoginForm - An organism component for user authentication.
 *
 * Combines multiple molecules (Input, PasswordInput, Button) with business logic
 * for user login, form validation, and error handling.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Input } from '@lean-agility/design-system/react';
import { Button } from '@lean-agility/design-system/react';
import { InlineError } from '@lean-agility/design-system/react';
import { PasswordInput } from '../molecules/PasswordInput';
import { loginSchema, type LoginFormData } from '../../validation/authSchemas';
import type { LoginCredentials } from '../../types';

export interface LoginFormProps {
  /**
   * Callback invoked when the form is successfully submitted.
   * Receives the validated login credentials.
   */
  onSubmit: (credentials: LoginCredentials) => Promise<void> | void;

  /**
   * Whether the form is in a loading/submitting state.
   * @default false
   */
  isLoading?: boolean;

  /**
   * Error message to display above the form.
   * Typically used for API-level errors (e.g., "Invalid credentials").
   */
  error?: string | null;

  /**
   * Default email value (e.g., from URL params or saved preferences).
   */
  defaultEmail?: string;

  /**
   * Additional CSS classes for the form container.
   */
  className?: string;
}

export function LoginForm({ onSubmit, isLoading = false, error, defaultEmail, className }: LoginFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: defaultEmail || '',
      password: '',
    },
  });

  const handleFormSubmit = async (data: LoginFormData) => {
    await onSubmit({
      email: data.email,
      password: data.password,
    });
  };

  const isFormLoading = isLoading || isSubmitting;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className={className} noValidate>
      <div className="space-y-4">
        {/* Global error message */}
        {error && <InlineError error={error} />}

        {/* Email input */}
        <Input
          {...register('email')}
          type="email"
          label="Email"
          placeholder="you@example.com"
          autoComplete="email"
          error={errors.email?.message}
          disabled={isFormLoading}
          autoFocus
        />

        {/* Password input */}
        <PasswordInput
          {...register('password')}
          label="Password"
          placeholder="Enter your password"
          autoComplete="current-password"
          error={errors.password?.message}
          disabled={isFormLoading}
        />

        {/* Submit button */}
        <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isFormLoading}>
          {isFormLoading ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </Button>
      </div>
    </form>
  );
}
